import { Box, Typography } from "@mui/material";
import EmptyComponent from "../../../components/EmptyComponent";
import {
  FC,
  useImperativeHandle,
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { globalStyles } from "../../../styles/GlobalStyles";
import { useTranslation } from "react-i18next";
import CaseTimeline from "./CaseTimeline";
import { useParams } from "react-router";
import {
  VOICE_NOTE,
  CASE_TIMELINE_TYPE,
  MILESTONE_TIMELINE,
  VOICE_NOTE_TIMELINE,
  REVISION,
  MILESTONE_REVISION_LOG,
  MILESTONE_TRACKER_STEPS,
  OPTIONAL_MILESTONE_TRACKER_STEPS,
  OPTIONAL_REVISION,
  TIMER_TIMELINE,
  NOTIFICATION_TIMELINE,
  NOTIFICATION,
  NOTIFICATION_RECIPIENT_TYPE,
  CONFLICT_ALERT_TIMELINE,
  CONFLICT_ALERT_TYPE,
  MILESTONE,
  OPTIONAL_MILESTONE_ITEM,
} from "../types/CommonTypes";

//@ts-ignore
import { useScreenshot } from "use-react-screenshot";
import jsPDF from "jspdf";
import EditMilestoneTimeDrawer, {
  toggleEditMilestoneTimeDrawer,
} from "./EditMilestoneTimeDrawer";
import EditCaseCommentDrawer, {
  toggleEditCaseCommentDrawer,
} from "./EditCaseCommentDrawer";
import EditVoiceNoteDrawer, {
  toggleEditVoiceNoteDrawer,
} from "./EditVoiceNoteDrawer";
import useEventEmitter from "../../../hooks/useEventEmitter";
import {
  SAVE_AND_MOVE_NEXT_TIMELINE_EVENT,
  SaveAndMoveNextTimelinePayload,
} from "./SaveAndMoveNextTimeline";
import { DATE_TYPE } from "../../../utils/Types";
import useGetCaseSummaryQuery from "../hooks/useGetCaseSummaryQuery";
import getTimerTitle from "../helpers/getTimerTitle";
import stringToNumber from "../../../helpers/stringToNumber";
import EditTimerLabelDrawer, {
  toggleEditTimerLabelDrawer,
} from "./EditTimersLabelDrawer";
import RevisedMilestoneTimeDrawer from "./RevisedMilestoneTimeDrawer";
import RevisedCaseNoteDrawer from "./RevisedCaseNoteDrawer";
import useGetSmsDeliveryLogsInfiniteQuery from "../hooks/useGetSmsDeliveryLogsInfiniteQuery";
import usePatchUpdateCaseAutoClose from "../hooks/usePatchUpdateCaseAutoClose";
import Loader from "../../../components/Loader";
import { CustomSwitch } from "../../../uiStyles/CustomSwitch";
import useGetCaseDetailQuery from "../hooks/useGetCaseDetailQuery";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import { formatInTimeZone } from "date-fns-tz";
import { getSelectedHospitalValue } from "../../hospitalModule/hooks/useSelectedHospitalValue";
import { DEFAULT_TIME_ZONE } from "../../../utils/Constants";

interface CaseSummaryProps {
  caseDetail?: {
    mrn: string;
    assignedSurgeon: string;
    procedureName: string;
    otId: string;
  };
  caseSummaryRef?: any;
  isEditMode?: boolean;
  subTitle?: string;
}

export type CASE_TIMELINE_ITEM =
  | MILESTONE_TIMELINE
  | VOICE_NOTE_TIMELINE
  | TIMER_TIMELINE
  | NOTIFICATION_TIMELINE
  | CONFLICT_ALERT_TIMELINE;

const LINEAR_MILESTONE_ORDER: Array<MILESTONE_TRACKER_STEPS> = [
  MILESTONE_TRACKER_STEPS.ROOM_READY,
  MILESTONE_TRACKER_STEPS.WHEELS_IN,
  MILESTONE_TRACKER_STEPS.PATIENT_READY,
  MILESTONE_TRACKER_STEPS.PROCEDURE_START,
  MILESTONE_TRACKER_STEPS.PROCEDURE_END,
  MILESTONE_TRACKER_STEPS.READY_TO_EXIT,
  MILESTONE_TRACKER_STEPS.WHEELS_OUT,
  MILESTONE_TRACKER_STEPS.ROOM_CLEAN,
];

type LINEAR_MILESTONE_SLOT =
  | MILESTONE_TRACKER_STEPS
  | OPTIONAL_MILESTONE_TRACKER_STEPS
  | "ROOM_CLEAN_START"
  | "ROOM_CLEAN_END";

const TIMEOUT_MILESTONE_KEY = OPTIONAL_MILESTONE_TRACKER_STEPS.TIMEOUT_TIME;


const DEFAULT_VALIDATION_MILESTONE_PAIRS: Array<
  [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
> = [
  [MILESTONE_TRACKER_STEPS.ROOM_READY, MILESTONE_TRACKER_STEPS.WHEELS_IN],
  [MILESTONE_TRACKER_STEPS.WHEELS_IN, MILESTONE_TRACKER_STEPS.PATIENT_READY],
  [MILESTONE_TRACKER_STEPS.WHEELS_IN, TIMEOUT_MILESTONE_KEY],
  [TIMEOUT_MILESTONE_KEY, MILESTONE_TRACKER_STEPS.PROCEDURE_START],
  [
    MILESTONE_TRACKER_STEPS.PROCEDURE_START,
    MILESTONE_TRACKER_STEPS.PROCEDURE_END,
  ],
  [
    MILESTONE_TRACKER_STEPS.PROCEDURE_END,
    MILESTONE_TRACKER_STEPS.READY_TO_EXIT,
  ],
  [MILESTONE_TRACKER_STEPS.READY_TO_EXIT, MILESTONE_TRACKER_STEPS.WHEELS_OUT],
  [MILESTONE_TRACKER_STEPS.WHEELS_OUT, "ROOM_CLEAN_START"],
  ["ROOM_CLEAN_START", "ROOM_CLEAN_END"],
];


const DEFAULT_MINIMUM_GAP_VALIDATION_PAIRS: Array<
  [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
> = [
  [MILESTONE_TRACKER_STEPS.ROOM_READY, MILESTONE_TRACKER_STEPS.WHEELS_IN],
  [MILESTONE_TRACKER_STEPS.WHEELS_IN, MILESTONE_TRACKER_STEPS.PATIENT_READY],
  [TIMEOUT_MILESTONE_KEY, MILESTONE_TRACKER_STEPS.PROCEDURE_START],
  [
    MILESTONE_TRACKER_STEPS.PROCEDURE_START,
    MILESTONE_TRACKER_STEPS.PROCEDURE_END,
  ],
  [
    MILESTONE_TRACKER_STEPS.PROCEDURE_END,
    MILESTONE_TRACKER_STEPS.READY_TO_EXIT,
  ],
  [MILESTONE_TRACKER_STEPS.READY_TO_EXIT, MILESTONE_TRACKER_STEPS.WHEELS_OUT],
  ["ROOM_CLEAN_START", "ROOM_CLEAN_END"],
];

// Keyed by the arriving (target) slot only — minimumGap/minimumGapMinutes
// is a property of the arriving milestone itself, not of a specific pair.
// This matters once a skipped milestone re-links the chain to an earlier
// predecessor (see resolveEffectivePriorMilestone below): the threshold
// still belongs to the arriving milestone regardless of which predecessor
// it ends up compared against.
const DEFAULT_MINIMUM_GAP_PAIR_MINUTES = new Map<LINEAR_MILESTONE_SLOT, number>(
  DEFAULT_MINIMUM_GAP_VALIDATION_PAIRS.map(([, to]) => [to, 1])
);

const buildValidationPairsFromCaseDetail = (
  milestones: Array<MILESTONE>,
  optionalMilestones: Array<OPTIONAL_MILESTONE_ITEM>
): {
  validationMilestonePairs: Array<
    [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
  >;
  minimumGapPairMinutes: Map<LINEAR_MILESTONE_SLOT, number>;
} => {
  const orderedMilestones = [
    ...milestones.map((milestone) => ({
      slot: milestone.displayName as LINEAR_MILESTONE_SLOT,
      order: milestone.order,
      minimumGap: milestone.minimumGap,
      minimumGapMinutes: milestone.minimumGapMinutes,
    })),
    ...optionalMilestones.map((milestone) => ({
      slot: milestone.displayName as LINEAR_MILESTONE_SLOT,
      order: milestone.order,
      minimumGap: milestone.minimumGap,
      minimumGapMinutes: milestone.minimumGapMinutes,
    })),
  ].sort((a, b) => a.order - b.order);

  const validationMilestonePairs: Array<
    [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
  > = [];
  const minimumGapPairMinutes = new Map<LINEAR_MILESTONE_SLOT, number>();

  const addPair = (
    from: LINEAR_MILESTONE_SLOT,
    to: LINEAR_MILESTONE_SLOT,
    minimumGap?: boolean,
    minimumGapMinutes?: number
  ) => {
    validationMilestonePairs.push([from, to]);
    if (minimumGap && minimumGapMinutes) {
      minimumGapPairMinutes.set(to, minimumGapMinutes);
    }
  };

  let priorSlot: LINEAR_MILESTONE_SLOT | null = null;

  orderedMilestones.forEach((milestone) => {
    const slot = milestone.slot;

    if (slot === MILESTONE_TRACKER_STEPS.ROOM_CLEAN) {
      if (priorSlot) {
        addPair(
          priorSlot,
          "ROOM_CLEAN_START",
          milestone.minimumGap,
          milestone.minimumGapMinutes
        );
      }
      addPair(
        "ROOM_CLEAN_START",
        "ROOM_CLEAN_END",
        milestone.minimumGap,
        milestone.minimumGapMinutes
      );
      priorSlot = "ROOM_CLEAN_END";
      return;
    }

    if (priorSlot) {
      addPair(priorSlot, slot, milestone.minimumGap, milestone.minimumGapMinutes);
    }
    priorSlot = slot;
  });

  return { validationMilestonePairs, minimumGapPairMinutes };
};
const getLatestRevision = <
  T extends {
    createdAt?: DATE_TYPE;
    updatedAt?: DATE_TYPE;
    id?: number;
    milestoneRevisedEndTime?: DATE_TYPE;
  }
>(
  revisions?: T[]
): T | undefined => {
  return revisions?.reduce<T | undefined>((latest, current) => {
    if (!latest) return current;

    const latestTime = new Date(
      latest.createdAt ??
        latest.updatedAt ??
        latest.milestoneRevisedEndTime ??
        0
    ).getTime();

    const currentTime = new Date(
      current.createdAt ??
        current.updatedAt ??
        current.milestoneRevisedEndTime ??
        0
    ).getTime();

    if (currentTime > latestTime) return current;

    if (currentTime === latestTime && (current.id ?? 0) > (latest.id ?? 0)) {
      return current;
    }

    return latest;
  }, undefined);
};
const buildMilestonesList = (
  caseSummaryData: any,
  caseId: string | undefined,
  t: (key: string) => string
): Array<MILESTONE_TIMELINE> => {
  return (
    caseSummaryData?.milestoneRevisions?.flatMap((milestone: REVISION) => {
      const revisions = milestone.milestoneRevisionLogs ?? [];
      const latestRevision = getLatestRevision(revisions);

      const createMilestoneObject = (data?: {
        title?: string;
        revision?: Array<MILESTONE_REVISION_LOG>;
        timestamp?: DATE_TYPE;
        loggedBy?: string | null;
        createdAt?: DATE_TYPE;
      }): MILESTONE_TIMELINE => ({
        type: CASE_TIMELINE_TYPE.MILESTONE as const,
        title: data?.title ?? milestone.displayName,
        revision: data?.revision ?? revisions,
        timestamp: data?.timestamp ?? latestRevision?.milestoneRevisedEndTime,
        loggedBy: data?.loggedBy ?? latestRevision?.milestoneRevisedByUserName,
        milestoneId: milestone.milestoneId,
        milestoneUUID: milestone.milestoneUuid,
        isOptionalMilestone: false,
        caseId,
        milestoneKey: milestone.displayName,
        createdAt:
          data?.createdAt ??
          revisions
            ?.map((r) => r.createdAt)
            .filter(Boolean)
            .sort()[0],
        skipped: milestone.skipped,
        skipReason: milestone.skipReason,
        skipReasonNote: milestone.skipReasonNote,
      });

      if (
        milestone.displayName === MILESTONE_TRACKER_STEPS.ROOM_CLEAN &&
        revisions.length > 0
      ) {
        const roomCleanMilestones: Array<MILESTONE_TIMELINE> = [];

        const roomCleanStartRevision = revisions.filter(
          (revision) => revision.action === "start"
        );

        const roomCleanEndRevision = revisions.filter(
          (revision) => revision.action === "end"
        );

        const latestRoomCleanStartRevision = getLatestRevision(
          roomCleanStartRevision
        );
        const latestRoomCleanEndRevision =
          getLatestRevision(roomCleanEndRevision);
        if (latestRoomCleanStartRevision) {
          roomCleanMilestones.push(
            createMilestoneObject({
              title: t("Room_Clean_Start"),
              revision: roomCleanStartRevision,
              timestamp: latestRoomCleanStartRevision.milestoneRevisedStartTime,
              loggedBy: latestRoomCleanStartRevision.startTimeLoggedBy,
              createdAt: roomCleanStartRevision
                ?.map((r) => r.createdAt)
                .filter(Boolean)
                .sort()[0],
            })
          );
        }
        if (latestRoomCleanEndRevision) {
          roomCleanMilestones.push(
            createMilestoneObject({
              title: t("Room_Clean_End"),
              revision: roomCleanEndRevision,
              timestamp: latestRoomCleanEndRevision.milestoneRevisedEndTime,
              loggedBy: latestRoomCleanEndRevision.endTimeLoggedBy,
              createdAt: roomCleanEndRevision
                ?.map((r) => r.createdAt)
                .filter(Boolean)
                .sort()[0],
            })
          );
        }

        return roomCleanMilestones.length > 0
          ? roomCleanMilestones
          : createMilestoneObject();
      }

      return createMilestoneObject();
    }) ?? []
  );
};

const getMilestoneLinearSlot = (
  milestone: MILESTONE_TIMELINE
): LINEAR_MILESTONE_SLOT | null => {
  if (!milestone.milestoneKey) {
    return null;
  }

  if (milestone.milestoneKey !== MILESTONE_TRACKER_STEPS.ROOM_CLEAN) {
    return milestone.milestoneKey as LINEAR_MILESTONE_SLOT;
  }

  const revisions =
    (milestone.revision as Array<MILESTONE_REVISION_LOG> | undefined) ?? [];
  const hasStartAction = revisions.some((rev) => rev.action === "start");
  const hasEndAction = revisions.some((rev) => rev.action === "end");

  if (hasStartAction && !hasEndAction) {
    return "ROOM_CLEAN_START";
  }

  if (hasEndAction && !hasStartAction) {
    return "ROOM_CLEAN_END";
  }

  return MILESTONE_TRACKER_STEPS.ROOM_CLEAN;
};

const buildLatestMilestoneBySlot = (
  milestonesList: Array<MILESTONE_TIMELINE>
): Record<string, MILESTONE_TIMELINE> => {
  const latestMilestoneBySlot: Record<string, MILESTONE_TIMELINE> = {};

  milestonesList.forEach((m) => {
    const slot = getMilestoneLinearSlot(m);
    if (!slot) {
      return;
    }
    if (!m.timestamp) {
      return;
    }
    const currentTime = new Date(m.timestamp).getTime();
    const existing = latestMilestoneBySlot[slot];
    if (!existing) {
      latestMilestoneBySlot[slot] = m;
      return;
    }
    const existingTime = new Date(existing.timestamp as DATE_TYPE).getTime();
    if (currentTime > existingTime) {
      latestMilestoneBySlot[slot] = m;
    }
  });

  return latestMilestoneBySlot;
};

const getMilestoneFlagKey = (milestone: MILESTONE_TIMELINE): string => {
  const timestampPart = milestone.timestamp
    ? String(new Date(milestone.timestamp).getTime())
    : "";

  const slotPart =
    getMilestoneLinearSlot(milestone) ?? milestone.milestoneKey ?? "";

  return `${milestone.milestoneUUID}-${slotPart}-${timestampPart}`;
};

const getTimestampMs = (timestamp?: DATE_TYPE): number | null => {
  if (!timestamp) {
    return null;
  }
  const ms = new Date(timestamp).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const getAmPmPeriod = (timestamp?: DATE_TYPE): "AM" | "PM" | null => {
  const ms = getTimestampMs(timestamp);
  if (ms == null) {
    return null;
  }

  // Read the hour in the hospital's own timezone (matching how
  // MilestoneClockPicker authors these timestamps) rather than the
  // viewing browser's local timezone, which can differ from the hospital's
  // and misclassify a sequence violation as an AM/PM flip (or vice versa).
  const tz = getSelectedHospitalValue().timeZone ?? DEFAULT_TIME_ZONE;
  const hour = parseInt(formatInTimeZone(new Date(ms), tz, "HH"), 10);
  return hour >= 12 ? "PM" : "AM";
};

const hasAmPmFlip = (
  previousMilestone: MILESTONE_TIMELINE | undefined,
  currentMilestone: MILESTONE_TIMELINE | undefined
): boolean => {
  const previousPeriod = getAmPmPeriod(previousMilestone?.timestamp);
  const currentPeriod = getAmPmPeriod(currentMilestone?.timestamp);

  if (!previousPeriod || !currentPeriod) {
    return false;
  }

  return previousPeriod !== currentPeriod;
};

const pickLatestMilestone = (
  candidate: MILESTONE_TIMELINE,
  current: MILESTONE_TIMELINE | undefined
): MILESTONE_TIMELINE => {
  if (!current?.timestamp) {
    return candidate;
  }
  const candidateMs = getTimestampMs(candidate.timestamp);
  const currentMs = getTimestampMs(current.timestamp);
  if (candidateMs == null || currentMs == null) {
    return current;
  }
  return candidateMs > currentMs ? candidate : current;
};

const getRoomCleanActionType = (
  milestone: MILESTONE_TIMELINE
): "start" | "end" | null => {
  const revisions =
    (milestone.revision as Array<MILESTONE_REVISION_LOG> | undefined) ?? [];
  const hasStartAction = revisions.some((rev) => rev.action === "start");
  const hasEndAction = revisions.some((rev) => rev.action === "end");

  if (hasStartAction && !hasEndAction) {
    return "start";
  }
  if (hasEndAction && !hasStartAction) {
    return "end";
  }
  return null;
};

const buildHardBlockMilestones = (
  milestonesList: Array<MILESTONE_TIMELINE>
): Record<string, boolean> => {
  let procedureStart: MILESTONE_TIMELINE | undefined;
  let procedureEnd: MILESTONE_TIMELINE | undefined;
  let roomCleanStart: MILESTONE_TIMELINE | undefined;
  let roomCleanEnd: MILESTONE_TIMELINE | undefined;

  const hardBlockByKey: Record<string, boolean> = {};

  milestonesList.forEach((m) => {
    if (!m.timestamp) {
      return;
    }

    if (m.milestoneKey === MILESTONE_TRACKER_STEPS.PROCEDURE_START) {
      procedureStart = pickLatestMilestone(m, procedureStart);
      return;
    }

    if (m.milestoneKey === MILESTONE_TRACKER_STEPS.PROCEDURE_END) {
      procedureEnd = pickLatestMilestone(m, procedureEnd);
      return;
    }

    if (m.milestoneKey !== MILESTONE_TRACKER_STEPS.ROOM_CLEAN) {
      return;
    }

    const actionType = getRoomCleanActionType(m);
    if (actionType === "start") {
      roomCleanStart = pickLatestMilestone(m, roomCleanStart);
      return;
    }
    if (actionType === "end") {
      roomCleanEnd = pickLatestMilestone(m, roomCleanEnd);
    }
  });

  if (procedureStart?.timestamp && procedureEnd?.timestamp) {
    const startTime = new Date(procedureStart.timestamp).getTime();
    const endTime = new Date(procedureEnd.timestamp).getTime();
    if (
      !Number.isNaN(startTime) &&
      !Number.isNaN(endTime) &&
      endTime < startTime
    ) {
      hardBlockByKey[getMilestoneFlagKey(procedureEnd)] = true;
    }
  }

  if (roomCleanStart?.timestamp && roomCleanEnd?.timestamp) {
    const startTime = new Date(roomCleanStart.timestamp).getTime();
    const endTime = new Date(roomCleanEnd.timestamp).getTime();
    if (
      !Number.isNaN(startTime) &&
      !Number.isNaN(endTime) &&
      endTime < startTime
    ) {
      hardBlockByKey[getMilestoneFlagKey(roomCleanEnd)] = true;
    }
  }

  return hardBlockByKey;
};

const shouldFlagMilestonePair = (
  currentMilestone: MILESTONE_TIMELINE | undefined,
  nextMilestone: MILESTONE_TIMELINE | undefined
): boolean => {
  const prevMs = getTimestampMs(currentMilestone?.timestamp);
  const currMs = getTimestampMs(nextMilestone?.timestamp);

  if (prevMs == null || currMs == null) {
    return false;
  }
  return currMs < prevMs;
};

const shouldFlagSequencePair = (
  currentMilestone: MILESTONE_TIMELINE | undefined,
  nextMilestone: MILESTONE_TIMELINE | undefined
): boolean => {
  return (
    shouldFlagMilestonePair(currentMilestone, nextMilestone) &&
    !hasAmPmFlip(currentMilestone, nextMilestone)
  );
};

const shouldFlagAmPmPair = (
  currentMilestone: MILESTONE_TIMELINE | undefined,
  nextMilestone: MILESTONE_TIMELINE | undefined
): boolean => {
  return (
    shouldFlagMilestonePair(currentMilestone, nextMilestone) &&
    hasAmPmFlip(currentMilestone, nextMilestone)
  );
};

const shouldFlagMinimumGapPair = (
  currentMilestone: MILESTONE_TIMELINE | undefined,
  nextMilestone: MILESTONE_TIMELINE | undefined,
  minimumGapMinutes: number
): boolean => {
  const prevMs = getTimestampMs(currentMilestone?.timestamp);
  const currMs = getTimestampMs(nextMilestone?.timestamp);

  if (prevMs == null || currMs == null) {
    return false;
  }

  if (currMs < prevMs) {
    return false;
  }

  const diffMs = currMs - prevMs;

  return diffMs <= minimumGapMinutes * 60 * 1000;
};

const getFlagPairInfo = (
  currentMilestone: MILESTONE_TIMELINE | undefined,
  nextMilestone: MILESTONE_TIMELINE | undefined
): {
  targetMilestone: MILESTONE_TIMELINE;
  previousMilestone?: MILESTONE_TIMELINE;
} | null => {
  if (!currentMilestone || !nextMilestone) {
    return null;
  }

  return {
    targetMilestone: nextMilestone,
    previousMilestone: currentMilestone,
  };
};

const isMilestoneSkipped = (
  milestone: MILESTONE_TIMELINE | undefined
): boolean => Boolean(milestone?.skipped);

const buildPairFromMap = (
  validationMilestonePairs: Array<
    [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
  >
): Map<LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT> => {
  const pairFromMap = new Map<LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT>();
  validationMilestonePairs.forEach(([from, to]) => {
    if (!pairFromMap.has(to)) {
      pairFromMap.set(to, from);
    }
  });
  return pairFromMap;
};

const resolveEffectivePriorMilestone = (
  fromSlot: LINEAR_MILESTONE_SLOT,
  latestMilestoneBySlot: Record<string, MILESTONE_TIMELINE>,
  pairFromMap: Map<LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT>
): MILESTONE_TIMELINE | undefined => {
  let currentSlot: LINEAR_MILESTONE_SLOT | undefined = fromSlot;
  const visited = new Set<LINEAR_MILESTONE_SLOT>();

  while (
    currentSlot &&
    isMilestoneSkipped(latestMilestoneBySlot[currentSlot])
  ) {
    if (visited.has(currentSlot)) {
      return undefined;
    }
    visited.add(currentSlot);
    currentSlot = pairFromMap.get(currentSlot);
  }

  return currentSlot ? latestMilestoneBySlot[currentSlot] : undefined;
};

const getUpdatedPriorMilestoneTime = (
  nextTimelineItem: Extract<CASE_TIMELINE_ITEM, { type: "milestone" }>,
  saveAndNextPayload?: SaveAndMoveNextTimelinePayload | null
): DATE_TYPE | undefined => {
  if (
    !saveAndNextPayload?.savedMilestoneTime ||
    !nextTimelineItem.priorMilestoneName
  ) {
    return nextTimelineItem.priorMilestoneTime;
  }

  const matchesPriorMilestone =
    nextTimelineItem.priorMilestoneName ===
      saveAndNextPayload.savedMilestoneName ||
    nextTimelineItem.priorMilestoneName ===
      saveAndNextPayload.savedMilestoneKey;

  return matchesPriorMilestone
    ? (saveAndNextPayload.savedMilestoneTime as DATE_TYPE)
    : nextTimelineItem.priorMilestoneTime;
};

const buildAmPmFlaggedMilestones = (
  latestMilestoneBySlot: Record<string, MILESTONE_TIMELINE>,
  validationMilestonePairs: Array<
    [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
  >,
  pairFromMap: Map<LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT>
): Record<
  string,
  { priorMilestoneName?: string; priorMilestoneTime?: DATE_TYPE }
> => {
  const flaggedMilestones: Record<
    string,
    { priorMilestoneName?: string; priorMilestoneTime?: DATE_TYPE }
  > = {};

  validationMilestonePairs.forEach(([currentKey, nextKey]) => {
    const nextMilestone = latestMilestoneBySlot[nextKey];

    // A skipped milestone participates in no validation.
    if (isMilestoneSkipped(nextMilestone)) {
      return;
    }

    const currentMilestone = resolveEffectivePriorMilestone(
      currentKey,
      latestMilestoneBySlot,
      pairFromMap
    );

    if (!shouldFlagAmPmPair(currentMilestone, nextMilestone)) {
      return;
    }

    const pairInfo = getFlagPairInfo(currentMilestone, nextMilestone);

    if (!pairInfo) {
      return;
    }

    const { targetMilestone, previousMilestone } = pairInfo;
    const key = getMilestoneFlagKey(targetMilestone);

    flaggedMilestones[key] = {
      priorMilestoneName:
        previousMilestone?.title ?? previousMilestone?.milestoneKey,
      priorMilestoneTime: previousMilestone?.timestamp as DATE_TYPE,
    };
  });

  return flaggedMilestones;
};

const buildSequenceFlaggedMilestones = (
  latestMilestoneBySlot: Record<string, MILESTONE_TIMELINE>,
  validationMilestonePairs: Array<
    [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
  >,
  pairFromMap: Map<LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT>
): Record<
  string,
  { priorMilestoneName?: string; priorMilestoneTime?: DATE_TYPE }
> => {
  const flaggedMilestones: Record<
    string,
    { priorMilestoneName?: string; priorMilestoneTime?: DATE_TYPE }
  > = {};

  validationMilestonePairs.forEach(([currentKey, nextKey]) => {
    const nextMilestone = latestMilestoneBySlot[nextKey];

    // A skipped milestone participates in no validation.
    if (isMilestoneSkipped(nextMilestone)) {
      return;
    }

    const currentMilestone = resolveEffectivePriorMilestone(
      currentKey,
      latestMilestoneBySlot,
      pairFromMap
    );

    if (!shouldFlagSequencePair(currentMilestone, nextMilestone)) {
      return;
    }

    const pairInfo = getFlagPairInfo(currentMilestone, nextMilestone);

    if (!pairInfo) {
      return;
    }

    const { targetMilestone, previousMilestone } = pairInfo;
    const key = getMilestoneFlagKey(targetMilestone);

    flaggedMilestones[key] = {
      priorMilestoneName:
        previousMilestone?.title ?? previousMilestone?.milestoneKey,
      priorMilestoneTime: previousMilestone?.timestamp as DATE_TYPE,
    };
  });

  return flaggedMilestones;
};

const buildMinimumGapFlaggedMilestones = (
  latestMilestoneBySlot: Record<string, MILESTONE_TIMELINE>,
  validationMilestonePairs: Array<
    [LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT]
  >,
  minimumGapPairMinutes: Map<LINEAR_MILESTONE_SLOT, number>,
  pairFromMap: Map<LINEAR_MILESTONE_SLOT, LINEAR_MILESTONE_SLOT>
): Record<
  string,
  {
    priorMilestoneName?: string;
    priorMilestoneTime?: DATE_TYPE;
    minimumGapMinutes?: number;
  }
> => {
  const flaggedMilestones: Record<
    string,
    {
      priorMilestoneName?: string;
      priorMilestoneTime?: DATE_TYPE;
      minimumGapMinutes?: number;
    }
  > = {};

  validationMilestonePairs.forEach(([currentKey, nextKey]) => {
    // Keyed by the arriving (target) slot — see the note on
    // DEFAULT_MINIMUM_GAP_PAIR_MINUTES above.
    const minimumGapMinutes = minimumGapPairMinutes.get(nextKey);

    if (minimumGapMinutes === undefined) {
      return;
    }

    const targetMilestone = latestMilestoneBySlot[nextKey];

    // A skipped milestone participates in no validation.
    if (isMilestoneSkipped(targetMilestone)) {
      return;
    }

    const previousMilestone = resolveEffectivePriorMilestone(
      currentKey,
      latestMilestoneBySlot,
      pairFromMap
    );

    if (
      !shouldFlagMinimumGapPair(
        previousMilestone,
        targetMilestone,
        minimumGapMinutes
      )
    ) {
      return;
    }

    const key = getMilestoneFlagKey(targetMilestone);

    flaggedMilestones[key] = {
      priorMilestoneName:
        previousMilestone?.title ?? previousMilestone?.milestoneKey,
      priorMilestoneTime: previousMilestone?.timestamp as DATE_TYPE,
      minimumGapMinutes,
    };
  });

  return flaggedMilestones;
};

const applyPriorityFlagsToMilestone = (
  milestone: MILESTONE_TIMELINE,
  {
    amPmFlag,
    sequenceFlag,
    minGapFlag,
    hardBlockFlag,
  }: {
    amPmFlag?: { priorMilestoneName?: string; priorMilestoneTime?: DATE_TYPE };
    sequenceFlag?: {
      priorMilestoneName?: string;
      priorMilestoneTime?: DATE_TYPE;
    };
    minGapFlag?: {
      priorMilestoneName?: string;
      priorMilestoneTime?: DATE_TYPE;
      minimumGapMinutes?: number;
    };
    hardBlockFlag?: boolean;
  }
): MILESTONE_TIMELINE => {
  const updatedMilestone: MILESTONE_TIMELINE = {
    ...milestone,
  };

  // Keep the lowest-priority context first so the highest-priority
  // validation details win when multiple flags exist on the same milestone.
  if (amPmFlag) {
    updatedMilestone.isAmPmCheck = true;
    updatedMilestone.priorMilestoneName = amPmFlag.priorMilestoneName;
    updatedMilestone.priorMilestoneTime = amPmFlag.priorMilestoneTime;
  }

  if (sequenceFlag) {
    updatedMilestone.isEarlierThanPrior = true;
    updatedMilestone.priorMilestoneName = sequenceFlag.priorMilestoneName;
    updatedMilestone.priorMilestoneTime = sequenceFlag.priorMilestoneTime;
  }

  if (minGapFlag) {
    updatedMilestone.isMinimumGapViolation = true;
    updatedMilestone.priorMilestoneName = minGapFlag.priorMilestoneName;
    updatedMilestone.priorMilestoneTime = minGapFlag.priorMilestoneTime;
    updatedMilestone.minimumGapMinutes = minGapFlag.minimumGapMinutes;
  }

  if (hardBlockFlag) {
    updatedMilestone.isHardBlock = true;
  }

  return updatedMilestone;
};

const CaseSummary: FC<CaseSummaryProps> = ({
  caseDetail,
  caseSummaryRef,
  isEditMode = false,
  subTitle = "Milestones",
}) => {
  const { t } = useTranslation();
  const [saveAndNextPayload, setSaveAndNextPayload] =
    useState<SaveAndMoveNextTimelinePayload | null>(null);
  const [hasAutoCloseReset, setHasAutoCloseReset] = useState(false);

  const { caseId } = useParams();
  const screenshotRef = useRef(null);

  const { mrn, assignedSurgeon, procedureName, otId } = caseDetail ?? {};

  const { tenantId, hospitalId } = useAuthValue();
  const { data: caseDetailData } = useGetCaseDetailQuery(
    tenantId,
    hospitalId,
    caseId ? parseInt(caseId) : undefined
  );

  const {
    data: caseSummaryData,
    isLoading: isCaseSummaryLoading,
    isError: isCaseSummaryError,
    refetch: refetchCaseSummary,
  } = useGetCaseSummaryQuery(caseId);
  const {
    mutate: patchUpdateCaseAutoClose,
    isPending: isUpdatingCaseAutoClose,
  } = usePatchUpdateCaseAutoClose(() => {
    setHasAutoCloseReset(true);
    refetchCaseSummary();
  });

  // Event listener for Save and Move Next functionality
  useEventEmitter(
    SAVE_AND_MOVE_NEXT_TIMELINE_EVENT,
    (payload: number | SaveAndMoveNextTimelinePayload) => {
      if (typeof payload === "number") {
        setSaveAndNextPayload({ timelineItemIndex: payload });
        return;
      }

      setSaveAndNextPayload(payload);
    }
  );
  const { data: smsDeliveryLogsListData, isLoading: isSmsDeliveryLogsLoading } =
    useGetSmsDeliveryLogsInfiniteQuery(caseId);

  const { validationMilestonePairs, minimumGapPairMinutes } = useMemo(() => {
    const milestones = caseDetailData?.procedure?.milestones;
    if (!milestones) {
      return {
        validationMilestonePairs: DEFAULT_VALIDATION_MILESTONE_PAIRS,
        minimumGapPairMinutes: DEFAULT_MINIMUM_GAP_PAIR_MINUTES,
      };
    }
    return buildValidationPairsFromCaseDetail(
      milestones,
      caseDetailData?.procedure?.optionalMilestones ?? []
    );
  }, [caseDetailData]);

  // The case's own `required` flag (on procedure.milestones) is
  // authoritative for the "missing required milestone" check below; falls
  // back to the prior static-array heuristic only while caseDetailData
  // hasn't loaded yet.
  const requiredMilestoneNames = useMemo(() => {
    const milestones = caseDetailData?.procedure?.milestones;
    if (!milestones) {
      return null;
    }
    return new Set(
      milestones
        .filter((milestone) => milestone.required)
        .map((milestone) => milestone.displayName)
    );
  }, [caseDetailData]);

  // Simplified timeline data creation
  const { caseTimelineData, additionalMilestonesList } = useMemo(() => {
    const milestonesList = buildMilestonesList(caseSummaryData, caseId, t);

    const additionalMilestonesRaw: Array<MILESTONE_TIMELINE> =
      (caseSummaryData?.optionalMilestones ?? [])?.map(
        (am: OPTIONAL_REVISION) => {
          const revisions = am?.revisions ?? am?.milestoneRevisionLogs;
          const title = am.milestoneName ?? am.displayName;
          const latestRevision = getLatestRevision(revisions);
          return {
            type: CASE_TIMELINE_TYPE.MILESTONE as const,
            title,
            revision: revisions,
            timestamp: latestRevision?.milestoneRevisedEndTime,
            loggedBy: latestRevision?.milestoneRevisedByUserName,
            milestoneId: am.milestoneId,
            milestoneUUID: am.milestoneUuid,
            milestoneKey: am.displayName,
            isOptionalMilestone: true,
            caseId,
            otId,
            createdAt: (latestRevision?.createdAt ??
              latestRevision?.updatedAt ??
              latestRevision?.milestoneRevisedEndTime) as DATE_TYPE,
            skipped: am.skipped,
            skipReason: am.skipReason,
            skipReasonNote: am.skipReasonNote,
          };
        }
      ) ?? [];

    const milestonesForValidation = [
      ...milestonesList,
      ...additionalMilestonesRaw,
    ];

    const latestMilestoneBySlot = buildLatestMilestoneBySlot(
      milestonesForValidation
    );

    const pairFromMap = buildPairFromMap(validationMilestonePairs);

    const amPmFlaggedMilestones = buildAmPmFlaggedMilestones(
      latestMilestoneBySlot,
      validationMilestonePairs,
      pairFromMap
    );

    const sequenceFlaggedMilestones = buildSequenceFlaggedMilestones(
      latestMilestoneBySlot,
      validationMilestonePairs,
      pairFromMap
    );

    const minimumGapFlaggedMilestones = buildMinimumGapFlaggedMilestones(
      latestMilestoneBySlot,
      validationMilestonePairs,
      minimumGapPairMinutes,
      pairFromMap
    );

    const hardBlockByKey = buildHardBlockMilestones(milestonesList);

    const applyFlags = (m: MILESTONE_TIMELINE): MILESTONE_TIMELINE => {
      const flagKey = getMilestoneFlagKey(m);
      const amPmFlag = amPmFlaggedMilestones[flagKey];
      const sequenceFlag = sequenceFlaggedMilestones[flagKey];
      const minGapFlag = minimumGapFlaggedMilestones[flagKey];
      const hardBlockFlag = hardBlockByKey[flagKey];

      if (!amPmFlag && !sequenceFlag && !minGapFlag && !hardBlockFlag) {
        return m;
      }

      return applyPriorityFlagsToMilestone(m, {
        amPmFlag,
        sequenceFlag,
        minGapFlag,
        hardBlockFlag,
      });
    };

    const milestonesWithFlags: Array<MILESTONE_TIMELINE> =
      milestonesList.map(applyFlags);

    const additionalMilestonesWithFlags: Array<MILESTONE_TIMELINE> =
      additionalMilestonesRaw.map(applyFlags);

    const voiceNotesList: Array<VOICE_NOTE_TIMELINE> =
      caseSummaryData?.caseNotes?.map((caseNote: VOICE_NOTE) => ({
        type: CASE_TIMELINE_TYPE.VOICE_NOTE,
        id: caseNote.id,
        note: caseNote.note,
        timestamp: caseNote.timestamp,
        updatedAt: caseNote.updatedAt,
        loggedBy: caseNote.loggedBy,
        classifications: caseNote.classifications,
        revisionLogs: caseNote.revisionLogs ?? [],
        caseId,
        createdAt: caseNote.timestamp,
      })) ?? [];

    const timerNotesList: Array<TIMER_TIMELINE> =
      caseSummaryData?.timers?.flatMap(
        ({ timerlogs, description, duration, createdAt, type: timerType }) =>
          timerlogs?.map((log) => {
            const { userName, timestamp, id, newStatus, metaData, timerId } =
              log ?? {};
            return {
              type: CASE_TIMELINE_TYPE.TIMER,
              timerType,
              id,
              timerId,
              timerCreatedAt: createdAt,
              title: getTimerTitle(newStatus, metaData?.action, timerType),
              timestamp,
              loggedBy: userName,
              desc: description,
              duration: stringToNumber(duration),
              caseId,
              action: metaData?.action,
              createdAt: timestamp,
            };
          })
      ) ?? [];

    const notificationList: Array<NOTIFICATION_TIMELINE> =
      caseSummaryData?.notifications?.map(
        ({
          id,
          recipientType,
          recipients,
          createdAt,
          message,
          loggedBy,
          recipientValue,
        }: NOTIFICATION) => {
          let sendTo: string;
          if (recipientType === NOTIFICATION_RECIPIENT_TYPE.USER) {
            const userName = recipients[0]?.userName || "";
            const additionalRecipients =
              recipients.length > 1
                ? ` and ${recipients.length - 1} more.`
                : "";
            sendTo = userName + additionalRecipients;
          } else {
            sendTo = recipientValue;
          }
          return {
            type: CASE_TIMELINE_TYPE.NOTIFICATION,
            id,
            sendTo,
            createdAt,
            message,
            loggedBy,
          };
        }
      ) ?? [];

    const smsDeliveryLogsList: Array<CONFLICT_ALERT_TIMELINE> =
      smsDeliveryLogsListData?.pages
        ?.flatMap((page) => page?.data)
        .filter((smsDeliveryLog) => {
          const validConflictAlertTypes = new Set<string>([
            CONFLICT_ALERT_TYPE.ANESTHETIST_NEXT_CASE_IMPACT,
            CONFLICT_ALERT_TYPE.SURGEON_NEXT_CASE_IMPACT,
            CONFLICT_ALERT_TYPE.EQUIPMENT_CONFLICT,
          ]);
          return (
            validConflictAlertTypes.has(smsDeliveryLog.type) &&
            smsDeliveryLog?.sentAt
          );
        })
        .map((smsDeliveryLog) => {
          return {
            type: CASE_TIMELINE_TYPE.CONFLICT_ALERT,
            conflictType: smsDeliveryLog?.type,
            id: smsDeliveryLog?.id,
            createdAt: smsDeliveryLog?.sentAt as DATE_TYPE,
            sentTo: smsDeliveryLog?.recipients,
          };
        }) ?? [];

    const timelineData = [
      ...milestonesWithFlags,
      ...voiceNotesList,
      ...timerNotesList,
      ...notificationList,
      ...smsDeliveryLogsList,
    ].sort((a, b) => {
      const getTimestamp = (item: CASE_TIMELINE_ITEM) =>
        new Date(item.createdAt).getTime();

      return getTimestamp(a) - getTimestamp(b);
    });

    return {
      caseTimelineData: timelineData,
      additionalMilestonesList: additionalMilestonesWithFlags,
    };
  }, [
    caseId,
    caseSummaryData,
    otId,
    smsDeliveryLogsListData?.pages,
    t,
    validationMilestonePairs,
    minimumGapPairMinutes,
  ]);

  const milestoneValidationSummary = useMemo(() => {
    const milestones = [
      ...caseTimelineData,
      ...additionalMilestonesList,
    ].filter(
      (item): item is MILESTONE_TIMELINE =>
        item.type === CASE_TIMELINE_TYPE.MILESTONE
    );

    const missingRequired = milestones.filter((m) => {
      if (!m.milestoneKey) {
        return false;
      }
      const isRequired = requiredMilestoneNames
        ? requiredMilestoneNames.has(m.milestoneKey)
        : LINEAR_MILESTONE_ORDER.includes(
            m.milestoneKey as MILESTONE_TRACKER_STEPS
          );
      if (!isRequired) {
        return false;
      }
      return !m.timestamp;
    });

    const sequenceIssues = milestones.filter(
      (m) =>
        m.isEarlierThanPrior &&
        m.timestamp &&
        m.priorMilestoneName &&
        m.priorMilestoneTime
    );

    const amPmIssues = milestones.filter(
      (m) =>
        m.isAmPmCheck &&
        m.timestamp &&
        m.priorMilestoneName &&
        m.priorMilestoneTime
    );

    const minimumGapIssues = milestones.filter(
      (m) =>
        m.isMinimumGapViolation &&
        m.timestamp &&
        m.priorMilestoneName &&
        m.priorMilestoneTime
    );

    const hasHardBlock = milestones.some((m) => m.isHardBlock);

    return {
      missingRequired,
      sequenceIssues,
      amPmIssues,
      minimumGapIssues,
      hasHardBlock,
    };
  }, [additionalMilestonesList, caseTimelineData, requiredMilestoneNames]);

  const comments = useMemo(() => {
    return caseSummaryData?.comments;
  }, [caseSummaryData]);

  const handleMilestoneNext = useCallback(
    (
      nextTimelineItem: Extract<CASE_TIMELINE_ITEM, { type: "milestone" }>,
      nextTimelineIndex: number,
      currentSaveAndNextPayload?: SaveAndMoveNextTimelinePayload | null
    ) => {
      // nextTimelineItem.priorMilestoneName/Time only exist when this
      // milestone participates in the minimumGap-validated chain (see
      // buildValidationPairsFromConfig above) — a milestone outside that
      // chain has neither, so minTime would reach the clock picker empty,
      // silently disabling its day-rollover protection. Fall back to the
      // milestone immediately before this one in the rendered timeline,
      // matching the fallback MilestoneTimeline's direct-click handler uses.
      const priorTimelineItem = caseTimelineData[nextTimelineIndex - 1];
      const fallbackPriorMilestone =
        priorTimelineItem?.type === CASE_TIMELINE_TYPE.MILESTONE
          ? priorTimelineItem
          : undefined;

      toggleEditMilestoneTimeDrawer({
        caseId: caseId ? parseInt(caseId) : undefined,
        otId: nextTimelineItem.otId,
        isOptionalMilestone: nextTimelineItem.isOptionalMilestone,
        milestoneId: nextTimelineItem.milestoneId,
        milestoneUUID: nextTimelineItem.milestoneUUID,
        milestoneName: nextTimelineItem.title,
        timelineItemIndex: nextTimelineIndex,
        milestoneKey: nextTimelineItem.milestoneKey,
        revision: nextTimelineItem.revision,
        timestamp: nextTimelineItem.timestamp,
        priorMilestoneName:
          nextTimelineItem.priorMilestoneName ?? fallbackPriorMilestone?.title,
        priorMilestoneTime:
          getUpdatedPriorMilestoneTime(
            nextTimelineItem,
            currentSaveAndNextPayload
          ) ?? fallbackPriorMilestone?.timestamp,
      });
    },
    [caseId, caseTimelineData]
  );

  const handleVoiceNoteNext = useCallback(
    (
      nextTimelineItem: Extract<CASE_TIMELINE_ITEM, { type: "note" }>,
      nextTimelineIndex: number
    ) => {
      nextTimelineItem.id &&
        toggleEditVoiceNoteDrawer({
          caseId: caseId ? parseInt(caseId) : undefined,
          voiceNote: nextTimelineItem,
          timelineItemIndex: nextTimelineIndex,
        });
    },
    [caseId]
  );

  const handleTimerNext = useCallback(
    (
      nextTimelineItem: Extract<CASE_TIMELINE_ITEM, { type: "timer" }>,
      nextTimelineIndex: number
    ) => {
      toggleEditTimerLabelDrawer({
        caseId: caseId ? parseInt(caseId) : undefined,
        timerId: nextTimelineItem.timerId,
        description: nextTimelineItem.desc,
        timerDuration: nextTimelineItem.duration,
        timerCreatedAt: nextTimelineItem.timestamp,
        timelineItemIndex: nextTimelineIndex,
      });
    },
    [caseId]
  );

  const handleCommentsNext = useCallback(
    (nextTimelineIndex: number) => {
      toggleEditCaseCommentDrawer({
        caseId: caseId ? parseInt(caseId) : undefined,
        comments,
        timelineItemIndex: nextTimelineIndex,
      });
    },
    [caseId, comments]
  );

  const handleOptionalMilestoneNext = useCallback(
    (
      nextTimelineIndex: number,
      currentSaveAndNextPayload?: SaveAndMoveNextTimelinePayload | null
    ) => {
      const nextOptionalMilestoneTimelineIndex =
        nextTimelineIndex - (caseTimelineData.length + 1);
      const nextTimelineItem =
        additionalMilestonesList[nextOptionalMilestoneTimelineIndex];

      if (
        nextTimelineItem &&
        nextTimelineItem?.type === CASE_TIMELINE_TYPE.MILESTONE
      ) {
        // Same minTime fallback as handleMilestoneNext above — this
        // milestone may not participate in the minimumGap-validated chain.
        const priorTimelineItem =
          additionalMilestonesList[nextOptionalMilestoneTimelineIndex - 1];
        const fallbackPriorMilestone =
          priorTimelineItem?.type === CASE_TIMELINE_TYPE.MILESTONE
            ? priorTimelineItem
            : undefined;

        toggleEditMilestoneTimeDrawer({
          caseId: caseId ? parseInt(caseId) : undefined,
          otId: nextTimelineItem.otId,
          isOptionalMilestone: true,
          milestoneId: nextTimelineItem.milestoneId,
          milestoneName: nextTimelineItem.title,
          milestoneUUID: nextTimelineItem.milestoneUUID,
          timelineItemIndex: nextTimelineIndex,
          milestoneKey: nextTimelineItem.milestoneKey,
          revision: nextTimelineItem.revision,
          timestamp: nextTimelineItem.timestamp,
          priorMilestoneName:
            nextTimelineItem.priorMilestoneName ?? fallbackPriorMilestone?.title,
          priorMilestoneTime:
            getUpdatedPriorMilestoneTime(
              nextTimelineItem,
              currentSaveAndNextPayload
            ) ?? fallbackPriorMilestone?.timestamp,
        });
      }
    },
    [caseTimelineData.length, additionalMilestonesList, caseId]
  );

  // Save & Next functionality
  const handleSaveAndMoveNext = useCallback(
    (currentSaveAndNextPayload: SaveAndMoveNextTimelinePayload) => {
      const { timelineItemIndex } = currentSaveAndNextPayload;
      const timelineItemIndx = timelineItemIndex;
      const nextTimelineIndex = timelineItemIndx + 1;

      if (nextTimelineIndex < caseTimelineData.length) {
        const nextTimelineItem = caseTimelineData[nextTimelineIndex];
        switch (nextTimelineItem?.type) {
          case CASE_TIMELINE_TYPE.MILESTONE:
            handleMilestoneNext(
              nextTimelineItem,
              nextTimelineIndex,
              currentSaveAndNextPayload
            );
            break;
          case CASE_TIMELINE_TYPE.VOICE_NOTE:
            handleVoiceNoteNext(nextTimelineItem, nextTimelineIndex);
            break;
          case CASE_TIMELINE_TYPE.TIMER:
            handleTimerNext(nextTimelineItem, nextTimelineIndex);
            break;
          default:
            break;
        }
      }
      if (nextTimelineIndex === caseTimelineData.length) {
        handleCommentsNext(nextTimelineIndex);
      }
      if (nextTimelineIndex > caseTimelineData.length) {
        handleOptionalMilestoneNext(
          nextTimelineIndex,
          currentSaveAndNextPayload
        );
      }

      setSaveAndNextPayload(null);
    },
    [
      caseTimelineData,
      handleMilestoneNext,
      handleVoiceNoteNext,
      handleTimerNext,
      handleCommentsNext,
      handleOptionalMilestoneNext,
    ]
  );

  // Automatically open next item when timelineItemIndex changes
  useEffect(() => {
    if (saveAndNextPayload !== null) {
      handleSaveAndMoveNext(saveAndNextPayload);
    }
  }, [saveAndNextPayload, handleSaveAndMoveNext]);

  const [, takeScreenshot] = useScreenshot();
  const shouldShowAutoCloseToggle =
    caseSummaryData?.autoComplete === true && !hasAutoCloseReset;

  useImperativeHandle(
    caseSummaryRef,
    () => ({
      getShouldShowAutoCloseToggle() {
        return shouldShowAutoCloseToggle;
      },
      async exportCaseSummaryScreenshot() {
        if (!screenshotRef.current) return;

        await new Promise((resolve) => requestAnimationFrame(resolve));
        const img = await takeScreenshot(screenshotRef.current, {
          backgroundColor: null,
          useCORS: true,
        });

        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "pt",
          format: "a4" as const,
        });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const image = new Image();
        image.src = img;

        image.onload = () => {
          const ratio = Math.min(
            pdfWidth / image.width,
            pdfHeight / image.height
          );
          const scaledWidth = image.width * ratio;
          const scaledHeight = image.height * ratio;
          const x = (pdfWidth - scaledWidth) / 2;
          const y = 36;

          pdf.addImage(img, "PNG", x, y, scaledWidth, scaledHeight);

          const caseDetailText = `MRN ${mrn} | ${assignedSurgeon} | ${procedureName}`;
          pdf.setFontSize(28);
          pdf.setFont("", "bold", 700);
          pdf.setTextColor(0, 0, 0);
          pdf.text(caseDetailText, 16, 36);

          pdf.save("orva-case-summary.pdf");
        };

        image.onerror = () => console.error("Image failed to load.");
      },
      getMilestoneValidationSummary() {
        return milestoneValidationSummary;
      },
    }),
    [
      takeScreenshot,
      mrn,
      assignedSurgeon,
      procedureName,
      milestoneValidationSummary,
      shouldShowAutoCloseToggle,
    ]
  );

  const isLoading = isCaseSummaryLoading || isSmsDeliveryLogsLoading;
  let commentsText = "";

  if (comments?.length) {
    commentsText = comments;
  } else if (isEditMode) {
    commentsText = t("Add_your_comments_here");
  }
  if (isLoading) {
    return <Loader />;
  }
  if (isCaseSummaryError) {
    return (
      <EmptyComponent
        title="Failed_To_Load_Case_Summary"
        onRetry={() => refetchCaseSummary()}
      />
    );
  }

  return (
    <Box ref={screenshotRef} p={3} sx={globalStyles.row}>
      <Box flex={1.2} sx={{ mr: "16px" }}>
        {/* <Typography fontWeight={700} fontSize={18} mb={3}>
          {t(subTitle)}
        </Typography> */}
        <CaseTimeline data={caseTimelineData} isEditMode={isEditMode} />
      </Box>

      <Box flex={1}>
        {shouldShowAutoCloseToggle ? (
          <Box
            display="flex"
            sx={styles.autocloseCard}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={styles.autocloseTitle}>
              {t("Case_Auto_Close")}
            </Typography>

            <CustomSwitch
              checked={true}
              disabled={isUpdatingCaseAutoClose}
              onChange={(_event, checked) => {
                if (!checked && caseId) {
                  patchUpdateCaseAutoClose({ caseId });
                }
              }}
            />
          </Box>
        ) : null}
        <Box sx={styles.commentsCard}>
          <Typography sx={styles.commentsTitle}>{t("Comments")}</Typography>

          <Box
            onClick={() =>
              isEditMode &&
              toggleEditCaseCommentDrawer({
                caseId: caseId ? parseInt(caseId) : undefined,
                comments,
                timelineItemIndex: caseTimelineData?.length,
              })
            }
            sx={[styles.commentsContent, isEditMode && styles.commentsEditMode]}
          >
            <Typography sx={styles.commentsText}>
              {commentsText}
              </Typography>
          </Box>
        </Box>

        <Box sx={styles.additionalMilestonesCard}>
          <Typography sx={styles.additionalMilestonesTitle}>
            {t("Additional_Milestones")}
          </Typography>

          <CaseTimeline
            data={additionalMilestonesList}
            hideLine
            isEditMode={isEditMode}
          />
        </Box>
      </Box>

      <RevisedMilestoneTimeDrawer />
      <RevisedCaseNoteDrawer />
      <EditMilestoneTimeDrawer />
      <EditVoiceNoteDrawer />
      <EditCaseCommentDrawer />
      <EditTimerLabelDrawer />
    </Box>
  );
};

const styles = {
  additionalMilestonesCard: {
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    px: "16px",
    py: "14px",
  },

  additionalMilestonesTitle: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 700,
    color: "custom.foreground.primary",
    mb: "4px",
  },
  commentsCard: {
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "custom.background.primary",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    px: "16px",
    py: "16px",
    mb: "16px",
  },
  autocloseCard: {
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "custom.background.primary",
    border: "1px solid #E5E7EB",
    borderRadius: "16px",
    px: "14px",
    py: "14px",
    mb: "16px",
  },
  autocloseTitle: {
    fontSize: "16px",
    lineHeight: "20px",
    fontWeight: 700,
    color: "custom.foreground.primary",
  },

  commentsTitle: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 700,
    color: "custom.foreground.primary",
    mb: "12px",
  },

  commentsContent: {
    width: "100%",
    boxSizing: "border-box",

    border: "1px solid transparent",
    borderRadius: "6px",

    minHeight: 0,
  },

  commentsText: {
    fontSize: "12px",
    lineHeight: "17px",
    fontWeight: 400,

    color: "custom.foreground.secondary",

    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },

  commentsEditMode: {
    cursor: "pointer",

    "&:hover": {
      backgroundColor: "#F8F9FA",
    },

    "&:focus-within": {
      borderColor: "#79747E",
    },
  },
};

export default CaseSummary;
